import React from 'react';
import PropTypes from 'prop-types';
import BugZeroLogo from '../bz/BugZeroLogo';
import BugMark from '../bz/BugMark';
import ComingSoon from '../bz/ComingSoon';
import { PRODUCTS } from '../bz/products';
import * as Icons from '../bz/icons';

// Hidden developer route (/style-guide). A living catalogue of the BugZero
// Platform design system — tokens, bz-* atoms, and shared bz/ components — so
// they can be eyeballed in one place. Intentionally NOT linked anywhere in the
// app or sidebar. Reference: STYLEGUIDE.md.

const Section = ({ title, kicker, children }) => (
  <section style={{ marginBottom: 40 }}>
    <div className="bz-section-head" style={{ marginBottom: 16 }}>
      <div>
        {kicker && (
          <div className="bz-kicker" style={{ color: 'var(--prevent)' }}>
            {kicker}
          </div>
        )}
        <h2 className="bz-h2" style={{ marginTop: kicker ? 4 : 0 }}>
          {title}
        </h2>
      </div>
    </div>
    {children}
  </section>
);
Section.propTypes = {
  title: PropTypes.string.isRequired,
  kicker: PropTypes.string,
  children: PropTypes.node
};

const Swatch = ({ name, varName, ink }) => (
  <div className="bz-card" style={{ padding: 0, overflow: 'hidden' }}>
    <div style={{ height: 56, background: `var(${varName})`, borderBottom: '1px solid var(--line-soft)' }} />
    <div style={{ padding: '8px 10px' }}>
      <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, color: ink ? `var(${ink})` : 'var(--ink)' }}>{name}</div>
      <div className="bz-mono bz-muted" style={{ fontSize: 'var(--fs-xs)' }}>
        {varName}
      </div>
    </div>
  </div>
);
Swatch.propTypes = {
  name: PropTypes.string.isRequired,
  varName: PropTypes.string.isRequired,
  ink: PropTypes.string
};

const SwatchGrid = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
    {children}
  </div>
);
SwatchGrid.propTypes = { children: PropTypes.node };

const BRAND = [
  ['Orange 1', '--bz-orange-1'],
  ['Orange 2', '--bz-orange-2'],
  ['Orange 3', '--bz-orange-3'],
  ['Blue 1', '--bz-blue-1'],
  ['Blue 2', '--bz-blue-2'],
  ['Blue 3', '--bz-blue-3'],
  ['Tan 1', '--bz-tan-1'],
  ['Tan 2', '--bz-tan-2'],
  ['Tan 3', '--bz-tan-3']
];

const FUNCTIONAL = [
  ['bg', '--bg'],
  ['bg-soft', '--bg-soft'],
  ['bg-tan', '--bg-tan'],
  ['bg-elev', '--bg-elev'],
  ['line', '--line'],
  ['line-soft', '--line-soft'],
  ['line-strong', '--line-strong'],
  ['ink', '--ink'],
  ['ink-soft', '--ink-soft'],
  ['ink-muted', '--ink-muted'],
  ['ink-faint', '--ink-faint']
];

const SEMANTIC = [
  ['ok', '--ok'],
  ['ok-soft', '--ok-soft'],
  ['warn', '--warn'],
  ['warn-soft', '--warn-soft'],
  ['bad', '--bad'],
  ['bad-soft', '--bad-soft'],
  ['info', '--info'],
  ['info-soft', '--info-soft']
];

const SEVERITY = [
  ['critical', '--sev-critical'],
  ['high', '--sev-high'],
  ['med', '--sev-med'],
  ['low', '--sev-low'],
  ['info', '--sev-info']
];

const StyleGuide = () => (
  <div style={{ maxWidth: 1320, margin: '0 auto' }}>
    <div style={{ marginBottom: 32 }}>
      <div className="bz-kicker" style={{ color: 'var(--prevent)' }}>
        Developer reference
      </div>
      <h1 className="bz-h1" style={{ marginTop: 4 }}>
        BugZero Platform — Style Guide
      </h1>
      <p className="bz-muted" style={{ fontSize: 'var(--fs-md)', marginTop: 4, maxWidth: 720 }}>
        Living catalogue of design tokens, <code className="bz-mono">bz-*</code> atom classes and shared{' '}
        <code className="bz-mono">bz/</code> components. Hidden dev route — not linked in the app. See{' '}
        <code className="bz-mono">STYLEGUIDE.md</code> for the source of truth.
      </p>
    </div>

    <Section title="Brand" kicker="Identity">
      <div
        className="bz-card"
        style={{ padding: 24, display: 'flex', alignItems: 'center', gap: 40, flexWrap: 'wrap' }}
      >
        <div className="bz-stack" style={{ gap: 6 }}>
          <span className="bz-kicker">Logo</span>
          <BugZeroLogo height={28} />
        </div>
        <div className="bz-stack" style={{ gap: 6 }}>
          <span className="bz-kicker">Mark</span>
          <BugMark size={40} />
        </div>
      </div>
    </Section>

    <Section title="Color tokens" kicker="Tokens">
      <h3 className="bz-h3" style={{ marginBottom: 10 }}>
        Brand
      </h3>
      <SwatchGrid>
        {BRAND.map(([n, v]) => (
          <Swatch key={v} name={n} varName={v} />
        ))}
      </SwatchGrid>

      <h3 className="bz-h3" style={{ margin: '20px 0 10px' }}>
        Functional
      </h3>
      <SwatchGrid>
        {FUNCTIONAL.map(([n, v]) => (
          <Swatch key={v} name={n} varName={v} />
        ))}
      </SwatchGrid>

      <h3 className="bz-h3" style={{ margin: '20px 0 10px' }}>
        Semantic
      </h3>
      <SwatchGrid>
        {SEMANTIC.map(([n, v]) => (
          <Swatch key={v} name={n} varName={v} />
        ))}
      </SwatchGrid>

      <h3 className="bz-h3" style={{ margin: '20px 0 10px' }}>
        Severity scale
      </h3>
      <SwatchGrid>
        {SEVERITY.map(([n, v]) => (
          <Swatch key={v} name={n} varName={v} />
        ))}
      </SwatchGrid>

      <h3 className="bz-h3" style={{ margin: '20px 0 10px' }}>
        Product accents
      </h3>
      <SwatchGrid>
        {PRODUCTS.map(p => (
          <Swatch key={p.id} name={p.short} varName={p.accent.replace('var(', '').replace(')', '')} />
        ))}
      </SwatchGrid>
    </Section>

    <Section title="Typography" kicker="Type">
      <div className="bz-card" style={{ padding: 24 }}>
        <div className="bz-kicker" style={{ color: 'var(--prevent)' }}>
          Kicker / eyebrow
        </div>
        <h1 className="bz-h1">Heading 1 — bz-h1</h1>
        <h2 className="bz-h2">Heading 2 — bz-h2</h2>
        <h3 className="bz-h3">Heading 3 — bz-h3</h3>
        <p style={{ marginTop: 8 }}>Body text — default ink color and size.</p>
        <p className="bz-muted">Muted text — bz-muted, for secondary content.</p>
        <p className="bz-soft">Soft text — bz-soft, for tertiary content.</p>
        <p className="bz-mono" style={{ marginBottom: 0 }}>
          Monospace — bz-mono 1234567890
        </p>
      </div>
    </Section>

    <Section title="Buttons" kicker="Atoms">
      <div className="bz-card" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" className="bz-btn bz-btn--primary">
            Primary (navy)
          </button>
          <button type="button" className="bz-btn bz-btn--accent">
            Accent (orange)
          </button>
          <button type="button" className="bz-btn">
            Default
          </button>
          <button type="button" className="bz-btn bz-btn--ghost">
            Ghost
          </button>
          <button type="button" className="bz-btn bz-btn--sm bz-btn--primary">
            Small primary
          </button>
          <button type="button" className="bz-btn bz-btn--primary" disabled>
            Disabled
          </button>
        </div>

        <p className="bz-muted" style={{ fontSize: 'var(--fs-sm)', margin: 0 }}>
          Hover any button above to preview the effect. All buttons share one interaction model: <strong>hover</strong>{' '}
          shifts the background (<code className="bz-mono">bg-soft</code> for default/ghost, a darker shade for
          primary/accent), <strong>active</strong> nudges down <code className="bz-mono">translateY(0.5px)</code>, all
          eased over <code className="bz-mono">0.12s</code> (transform <code className="bz-mono">0.04s</code>).
        </p>

        {/* Static preview of each interaction state for the primary button (so the
            hover/active treatment is documented without requiring a real hover). */}
        <div>
          <div className="bz-kicker" style={{ marginBottom: 8 }}>
            Interaction states · primary
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {[
              ['Default', { background: 'var(--bz-blue-1)', borderColor: 'var(--bz-blue-1)' }],
              ['Hover', { background: '#1a2a36', borderColor: '#1a2a36' }],
              ['Active', { background: '#1a2a36', borderColor: '#1a2a36', transform: 'translateY(0.5px)' }],
              ['Disabled', { background: 'var(--bz-blue-1)', borderColor: 'var(--bz-blue-1)', opacity: 0.55 }]
            ].map(([label, style]) => (
              <div key={label} className="bz-stack" style={{ gap: 6, alignItems: 'center' }}>
                {/* Render the static state with inline styles; pointer-events off so
                    the live :hover/:active rules don't fight the documented snapshot. */}
                <span
                  className="bz-btn bz-btn--primary"
                  style={{ ...style, pointerEvents: 'none', color: 'var(--ink-invert)' }}
                >
                  Primary
                </span>
                <span className="bz-mono bz-muted" style={{ fontSize: 'var(--fs-xs)' }}>
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>

    <Section title="Pills & status chips" kicker="Atoms">
      <div className="bz-card" style={{ padding: 24, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <span className="bz-pill bz-pill--ok">
          <span className="bz-dot" /> Healthy
        </span>
        <span className="bz-pill bz-pill--warn">
          <span className="bz-dot" /> Warning
        </span>
        <span className="bz-pill bz-pill--bad">
          <span className="bz-dot" /> Critical
        </span>
        <span className="bz-pill bz-pill--info">
          <span className="bz-dot" /> Info
        </span>
        <span className="bz-pill bz-pill--prevent">Prevent</span>
        <span className="bz-pill bz-pill--plan">Plan</span>
        <span className="bz-pill bz-pill--predict">Predict</span>
        <span className="bz-pill">Neutral</span>
      </div>
    </Section>

    <Section title="Inputs" kicker="Atoms">
      <div
        className="bz-card"
        style={{ padding: 24, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}
      >
        <span className="bz-input">
          <Icons.IcoSearch size={14} style={{ color: 'var(--ink-muted)' }} />
          <input placeholder="Search…" aria-label="Search example" />
        </span>
        <span className="bz-input">
          <input placeholder="Plain text input" aria-label="Text example" />
        </span>
        <span className="bz-input">
          <select aria-label="Select example" defaultValue="">
            <option value="" disabled>
              Choose an option…
            </option>
            <option>Option A</option>
            <option>Option B</option>
          </select>
        </span>
      </div>
    </Section>

    <Section title="Stat tiles" kicker="Atoms">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
        {[
          ['Open bugs', '1,284', '+12 this week', 'var(--prevent)'],
          ['Critical', '23', '3 unassigned', 'var(--bad)'],
          ['Vendors', '14', 'all connected', 'var(--plan)'],
          ['Resolved', '92%', 'last 30 days', 'var(--ok)']
        ].map(([label, value, meta, accent]) => (
          <div key={label} className="bz-stat" style={{ borderTop: `3px solid ${accent}` }}>
            <div className="bz-stat__label">{label}</div>
            <div className="bz-stat__value">{value}</div>
            <div className="bz-stat__meta">{meta}</div>
          </div>
        ))}
      </div>
    </Section>

    <Section title="Progress / ratio bar" kicker="Atoms">
      <div className="bz-card" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {[
          ['72%', 0.72, 'var(--ok)'],
          ['45%', 0.45, 'var(--warn)'],
          ['18%', 0.18, 'var(--bad)']
        ].map(([label, ratio, color]) => (
          <div key={label} className="bz-row" style={{ gap: 12 }}>
            <div className="bz-bar" style={{ width: 240 }}>
              <span style={{ width: `${ratio * 100}%`, background: color }} />
            </div>
            <span className="bz-mono bz-muted" style={{ fontSize: 'var(--fs-sm)' }}>
              {label}
            </span>
          </div>
        ))}
      </div>
    </Section>

    <Section title="Table" kicker="Atoms">
      <div className="bz-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="bz-table">
          <thead>
            <tr>
              <th>Bug</th>
              <th>Vendor</th>
              <th>Severity</th>
              <th style={{ textAlign: 'right' }}>Age (days)</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['CVE-2025-14847', 'MongoDB', 'critical', 'bz-pill--bad', 3],
              ['CSCwq-88210', 'Cisco', 'high', 'bz-pill--warn', 11],
              ['MS-2025-0042', 'Microsoft', 'medium', 'bz-pill--info', 27]
            ].map(([id, vendor, sev, pill, age]) => (
              <tr key={id}>
                <td className="bz-mono">{id}</td>
                <td>{vendor}</td>
                <td>
                  <span className={`bz-pill ${pill}`}>{sev}</span>
                </td>
                <td className="bz-mono bz-tnum" style={{ textAlign: 'right' }}>
                  {age}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>

    <Section title="Cards" kicker="Atoms">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
        <div className="bz-card" style={{ padding: 20 }}>
          <h3 className="bz-h3" style={{ marginTop: 0 }}>
            bz-card
          </h3>
          <p className="bz-muted" style={{ margin: 0 }}>
            Elevated container with shadow and border.
          </p>
        </div>
        <div className="bz-card bz-card--flat" style={{ padding: 20 }}>
          <h3 className="bz-h3" style={{ marginTop: 0 }}>
            bz-card--flat
          </h3>
          <p className="bz-muted" style={{ margin: 0 }}>
            Flat container — no shadow.
          </p>
        </div>
      </div>
    </Section>

    <Section title="Icons" kicker="bz/icons">
      <div className="bz-card" style={{ padding: 24 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 16 }}>
          {Object.keys(Icons)
            .filter(k => k.startsWith('Ico'))
            .map(name => {
              const Ico = Icons[name];
              return (
                <div
                  key={name}
                  className="bz-stack"
                  style={{ alignItems: 'center', gap: 8, textAlign: 'center', color: 'var(--ink)' }}
                >
                  <Ico size={22} />
                  <span className="bz-mono bz-muted" style={{ fontSize: 'var(--fs-xs)' }}>
                    {name}
                  </span>
                </div>
              );
            })}
        </div>
      </div>
    </Section>

    <Section title="Product accents" kicker="bz/products">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
        {PRODUCTS.map(p => {
          const Ico = p.Icon;
          return (
            <div key={p.id} className="bz-card" style={{ padding: 20, borderTop: `3px solid ${p.accent}` }}>
              <div className="bz-row" style={{ gap: 10, alignItems: 'center', color: p.accent, marginBottom: 8 }}>
                <Ico size={20} />
                <span className="bz-h3" style={{ margin: 0, color: 'var(--ink)' }}>
                  {p.name}
                </span>
              </div>
              <div className="bz-muted" style={{ fontSize: 'var(--fs-sm)' }}>
                {p.blurb}
              </div>
            </div>
          );
        })}
      </div>
    </Section>

    <Section title="ComingSoon placeholder" kicker="bz/ComingSoon">
      <ComingSoon
        title="Example gated feature"
        summary="Use <ComingSoon> for any UI that isn't wired to real data yet. Never ship mock data to a real tenant."
        planned={['Wire to BZAPIService', 'Flip the status gate', 'Move the row to Shipped in COMING_SOON.md']}
      />
    </Section>
  </div>
);

export default StyleGuide;
